// Yup schemas for the projects domain (projects, phases, buildings,
// configurations). Mirrors BE Pydantic shapes in
// services/shared/shared_layer/schemas/property.py.

import * as yup from "yup";

/**
 * POST/PUT /projects/buildings — create or update a building under a phase.
 * Mirrors RequestBuildingData. Number-of-floors is a string in the form
 * input but coerced to integer at submit time via yup transform.
 */
export const buildingSchema = yup.object({
  buildingName: yup.string().trim().required("Building name is required"),
  numberOfFloors: yup
    .number()
    .typeError("Number of floors must be a number")
    .integer("Must be a whole number")
    .min(1, "Must be at least 1 floor")
    .required("Number of floors is required"),
  constructionLinkedPlanId: yup
    .number()
    .typeError("Pick a construction plan")
    .required("Construction plan is required"),
});

/**
 * POST /building/property/config — assign a list of property configs to a
 * building. The modal collects an array of config IDs; the BE expects them
 * wrapped into [{ buildingId, propertyConfigId }, ...] by the caller.
 * Validation here ensures at least one config is picked.
 */
export const assignConfigToBuildingSchema = yup.object({
  configIds: yup
    .array()
    .of(yup.number().required())
    .min(1, "Select at least one configuration")
    .required(),
});

/**
 * POST/PUT /projects/phase — create or update a project phase. Field names
 * mirror the BE multipart form keys (snake_case). Submitted as FormData
 * because of the attached `images` files.
 *
 * `phaseStatus` is a 1–4 enum:
 *   1 = Active, 2 = Completed, 3 = On Hold, 4 = Cancelled
 */
export const phaseSchema = yup.object({
  name: yup.string().trim().required("Phase name is required"),
  reraNo: yup.string().trim().nullable(),
  reraStartDate: yup.string().nullable(),
  reraEndDate: yup
    .string()
    .nullable()
    .test("after-start", "End date must be after start date", function (value) {
      const start = this.parent.reraStartDate;
      if (!value || !start) return true; // either missing → schema-level required handles
      return new Date(value) >= new Date(start);
    }),
  towers: yup
    .number()
    .typeError("Towers must be a number")
    .integer("Must be a whole number")
    .min(0, "Cannot be negative")
    .required("Number of towers is required"),
  units: yup
    .number()
    .typeError("Units must be a number")
    .integer("Must be a whole number")
    .min(0, "Cannot be negative")
    .required("Number of units is required"),
  configuration: yup.string().nullable(),
  phaseStatus: yup
    .number()
    .oneOf([1, 2, 3, 4], "Invalid phase status")
    .required("Phase status is required"),
});

export const PHASE_STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Completed" },
  { value: 3, label: "On Hold" },
  { value: 4, label: "Cancelled" },
];

/**
 * POST/PUT /property/config — create or update a property configuration.
 *
 * Area fields come in sqft/sqmt pairs. We require sqft only; sqmt is derived
 * via the AreaPair composite at edit-time, so validating sqft is enough to
 * guarantee both are populated by the time submit fires. The same applies
 * to superBuiltup (required) and dry/balcony (optional, hidden for Shop).
 *
 * `sizeBhk` is only required when propertyType is NOT "Shop" — Yup's
 * `.when()` handles the conditional.
 */
export const propertyConfigSchema = yup.object({
  configName: yup.string().trim().required("Configuration name is required"),
  propertyType: yup
    .string()
    .oneOf(["Apartment", "Shop", "Land", "Independent"], "Pick a property type")
    .required("Property type is required"),
  sizeBhk: yup
    .number()
    .transform((v, orig) => (orig === "" || orig == null ? undefined : v))
    .when("propertyType", {
      is: (t) => t && t !== "Shop",
      then: (s) =>
        s
          .typeError("Size (BHK) must be a number")
          .integer("Must be a whole number")
          .min(1, "Must be at least 1")
          .required("Size (BHK) is required for non-Shop properties"),
      otherwise: (s) => s.notRequired(),
    }),
  carpetAreaSqft: yup
    .number()
    .typeError("Carpet area must be a number")
    .positive("Must be greater than 0")
    .required("Carpet area is required"),
  carpetAreaSqmt: yup.number().nullable(),
  superBuiltupAreaSqft: yup
    .number()
    .typeError("Super built-up area must be a number")
    .positive("Must be greater than 0")
    .required("Super built-up area is required"),
  superBuiltupAreaSqmt: yup.number().nullable(),
  dryBalconyAreaSqft: yup
    .number()
    .nullable()
    .transform((v, orig) => (orig === "" || orig == null ? null : v)),
  dryBalconyAreaSqmt: yup.number().nullable(),
  balconyAreaSqft: yup
    .number()
    .nullable()
    .transform((v, orig) => (orig === "" || orig == null ? null : v)),
  balconyAreaSqmt: yup.number().nullable(),
  basePrice: yup
    .number()
    .typeError("Base price must be a number")
    .positive("Must be greater than 0")
    .required("Base price is required"),
  currencyId: yup
    .number()
    .typeError("Pick a currency")
    .required("Currency is required"),
});

export const PROPERTY_TYPE_OPTIONS = [
  { value: "Apartment", label: "Apartment" },
  { value: "Shop", label: "Shop" },
  { value: "Land", label: "Land" },
  { value: "Independent", label: "Independent" },
];

/**
 * POST/PUT /construction/linked/plan — create or rename a construction plan.
 * Single-field form: just the plan name.
 */
export const constructionPlanSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required("Plan name is required")
    .max(100, "Plan name cannot exceed 100 characters"),
});

/**
 * POST/PUT /property/manual — single-property create/edit (ManualUpload).
 * Mirrors RequestManualPropertyData on the BE. Car parking is captured as an
 * array of strings; empty strings are filtered at submit time.
 */
export const manualPropertySchema = yup.object({
  buildingId: yup
    .number()
    .typeError("Pick a building")
    .required("Building is required"),
  propertyConfigId: yup
    .number()
    .typeError("Pick a configuration")
    .min(1, "Configuration is required")
    .required("Configuration is required"),
  propertyState: yup
    .string()
    .oneOf(["Under Construction", "Ready", ""], "Invalid state")
    .nullable(),
  propertyNo: yup.string().trim().nullable(),
  floor: yup
    .number()
    .typeError("Floor must be a number")
    .integer("Must be a whole number")
    .min(0, "Cannot be negative")
    .nullable()
    .transform((v, orig) => (orig === "" ? null : v)),
  floorRiseAmount: yup
    .number()
    .typeError("Floor rise must be a number")
    .min(0, "Cannot be negative")
    .nullable()
    .transform((v, orig) => (orig === "" ? null : v)),
  carParking: yup.array().of(yup.string()).default([]),
});

export const PROPERTY_STATE_OPTIONS = [
  { value: "Under Construction", label: "Under Construction" },
  { value: "Ready", label: "Ready" },
];

/**
 * Project Settings — Bank Details section. Mirrors the parseBankDetails /
 * formatBankDetailsString contract used by the BE (which stores bank details
 * as an encrypted comma-separated key:value string).
 */
export const projectBankDetailsSchema = yup.object({
  bankName: yup.string().trim().nullable(),
  bankAccount: yup.string().trim().nullable(),
  accountNo: yup.string().trim().nullable(),
  branch: yup.string().trim().nullable(),
  ifscCode: yup.string().trim().nullable(),
});

/**
 * Project Settings — Govt Charges section. Each percent list is capped at 3
 * entries on the FE; the BE doesn't enforce the cap. Empty arrays are valid.
 */
export const projectGovtChargesSchema = yup.object({
  stamp_duty_percent: yup
    .array()
    .of(yup.number().typeError("Must be a number").min(0))
    .max(3, "Maximum 3 stamp-duty options")
    .default([]),
  gst_percent: yup
    .array()
    .of(yup.number().typeError("Must be a number").min(0))
    .max(3, "Maximum 3 GST options")
    .default([]),
  registration_percent: yup
    .array()
    .of(yup.number().typeError("Must be a number").min(0))
    .max(3, "Maximum 3 registration options")
    .default([]),
});

export const BANK_ACCOUNT_TYPE_OPTIONS = [
  { value: "Savings", label: "Savings" },
  { value: "Current", label: "Current" },
];

/**
 * Project-level fields validated by ProjectDetailsForm. Address fields are
 * FLAT here (not nested under `address.*`), matching the legacy form state
 * shape the form already submits. The form has additional state-managed
 * sections (phases array, amenities, BOQ, file uploads) that aren't covered
 * by this schema — those are validated inline in the submit handler.
 */
export const projectDetailsSchema = yup.object({
  developer: yup.string().trim().required("Developer name is required").max(100),
  projectName: yup.string().trim().required("Project name is required").max(100),
  projectTowers: yup
    .number()
    .typeError("Number of towers must be a number")
    .integer("Must be a whole number")
    .min(0)
    .required("Project towers is required"),
  projectUnits: yup
    .number()
    .typeError("Number of units must be a number")
    .integer("Must be a whole number")
    .min(0)
    .nullable()
    .transform((v, orig) => (orig === "" ? null : v)),
  phoneNumber: yup.string().trim().max(20).nullable(),
  whatsappNumber: yup.string().trim().max(20).nullable(),
  location: yup.string().trim().max(100).nullable(),
  sizeAcres: yup
    .number()
    .typeError("Size must be a number")
    .min(0)
    .required("Size (acres) is required"),
  buildingNo: yup.string().trim().required("Building No. is required").max(100),
  streetName1: yup.string().trim().required("Street name 1 is required").max(100),
  streetName2: yup.string().trim().max(100).nullable(),
  city: yup.string().trim().required("City is required").max(100),
  district: yup.string().trim().required("District is required").max(100),
  state: yup.string().trim().required("State is required").max(100),
  pinCode: yup
    .string()
    .trim()
    .required("Pincode is required")
    .matches(/^\d{4,10}$/, "Pincode must be 4–10 digits"),
  country: yup.string().trim().required("Country is required").max(100),
  // Bank details: validated only for length; all optional.
  bankDetails: yup.object({
    bankName: yup.string().trim().max(100).nullable(),
    bankAccount: yup.string().trim().max(100).nullable(),
    accountNo: yup.string().trim().max(100).nullable(),
    branch: yup.string().trim().max(100).nullable(),
    ifscCode: yup.string().trim().max(100).nullable(),
  }),
});
